import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';
import { formatAddress } from '../../lib/utils';
import { Clock, User, Timer, Trophy, XCircle } from 'lucide-react';
import { formatDistanceToNow, isPast } from 'date-fns';

const STATUS_CONFIG = {
    open: { labelKey: 'betCard.statusOpen', color: 'bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/20', icon: Timer },
    active: { labelKey: 'betCard.statusActive', color: 'bg-[#6F3FCA]/20 text-[#9B6FE8] border-[#6F3FCA]/30', icon: Timer },
    in_assertion: { labelKey: 'betCard.statusInAssertion', color: 'bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/30', icon: Clock },
    resolved: { labelKey: 'betCard.statusResolved', color: 'bg-[#6F3FCA]/20 text-[#9B6FE8] border-[#6F3FCA]/30', icon: Trophy },
    cancelled: { labelKey: 'betCard.statusCancelled', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
};

export function BetDetailHeader({ bet, isCreator, isTaker, dateLocale }) {
    const { t } = useTranslation();
    const status = STATUS_CONFIG[bet.status] || STATUS_CONFIG.open;
    const StatusIcon = status.icon;
    const categoryKey = `bets.cat${(bet.category || 'custom').charAt(0).toUpperCase() + (bet.category || 'custom').slice(1)}`;

    return (
        <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
                <Badge variant="outline" className={status.color}>
                    <StatusIcon className="w-4 h-4 mr-1" />
                    {t(status.labelKey)}
                </Badge>
                <Badge variant="outline" className="bg-white/5 text-white/50 border-white/10 capitalize">
                    {t(categoryKey)}
                </Badge>
                {isCreator && (
                    <Badge className="bg-[#00FF66]/10 text-[#00FF66] border-0">{t('betDetail.yourBet')}</Badge>
                )}
                {isTaker && (
                    <Badge className="bg-[#6F3FCA]/20 text-[#9B6FE8] border-0">{t('betDetail.youTook')}</Badge>
                )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">{bet.description}</h1>
            <div className="flex items-center gap-4 text-white/50 text-sm">
                <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>
                        {isPast(new Date(bet.resolution_time)) ? t('betDetail.ended') : t('betDetail.ends')}{' '}
                        {formatDistanceToNow(new Date(bet.resolution_time), { addSuffix: true, locale: dateLocale })}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="font-mono">{formatAddress(bet.creator_address)}</span>
                </div>
            </div>
        </div>
    );
}
